import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const GA_SCRIPT_ID = 'ga-gtag';

// Initialize Google Analytics
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('Google Analytics Measurement ID not found');
    return;
  }

  // Load gtag script (once)
  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false, // We'll handle page views manually for SPA
  });
};

// Track page views
export const trackPageView = (path: string) => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
  });
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Track contact form submission
export const trackContactFormSubmission = (formType: string = 'contact') => {
  trackEvent('form_submission', 'Contact', formType);
};

// Track button clicks
export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent('click', 'Button', `${buttonName} - ${location}`);
};

// Track link clicks
export const trackLinkClick = (linkName: string, destination: string) => {
  trackEvent('click', 'Link', `${linkName} - ${destination}`);
};

// Track reference/portfolio views
export const trackReferenceView = (referenceName: string) => {
  trackEvent('view_item', 'Reference', referenceName);
};

// Hook for automatic page view tracking
export const usePageTracking = (enabled: boolean) => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;
    trackPageView(location.pathname + location.search);
  }, [location, enabled]);
};

// Hook for initializing GA on app mount
export const useGoogleAnalytics = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;
    initGA();
  }, [enabled]);
};
